/**
 * Dots & Boxes Game Persistence Service
 *
 * Auto-saves game state to local storage after each move.
 */

import {
  DotsBoxesGameState,
  DotsBoxesGameSettings,
  DotsBoxesDifficulty,
} from '../types';
import { LocalStorageManager } from '../../services/LocalStorageManager';
import { DataSynchronizationService } from '../../services/DataSynchronizationService';

export interface SavedDotsBoxesGame {
  gameId: string;
  mode: DotsBoxesGameSettings['mode'];
  gameState: DotsBoxesGameState;
  metadata: {
    aiDifficulty?: DotsBoxesDifficulty;
    startedAt: number;
    lastSavedAt: number;
  };
}

export class DotsBoxesGamePersistence {
  private storage: LocalStorageManager;
  private syncService: DataSynchronizationService | null;
  private readonly STORAGE_KEY = 'dots_boxes_saved_games';
  private readonly AUTO_SAVE_KEY = 'dots_boxes_auto_save';

  constructor(
    storage: LocalStorageManager,
    syncService?: DataSynchronizationService
  ) {
    this.storage = storage;
    this.syncService = syncService || null;
  }

  autoSave(
    mode: DotsBoxesGameSettings['mode'],
    gameState: DotsBoxesGameState,
    metadata: SavedDotsBoxesGame['metadata']
  ): boolean {
    try {
      const savedGame: SavedDotsBoxesGame = {
        gameId: 'auto_save',
        mode,
        gameState,
        metadata: { ...metadata, lastSavedAt: Date.now() },
      };
      localStorage.setItem(this.AUTO_SAVE_KEY, JSON.stringify(savedGame));
      return true;
    } catch (error) {
      console.error('Failed to auto-save dots-and-boxes game:', error);
      return false;
    }
  }

  loadAutoSave(): SavedDotsBoxesGame | null {
    try {
      const data = localStorage.getItem(this.AUTO_SAVE_KEY);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Failed to load dots-and-boxes auto-save:', error);
      return null;
    }
  }

  clearAutoSave(): boolean {
    try {
      localStorage.removeItem(this.AUTO_SAVE_KEY);
      return true;
    } catch (error) {
      console.error('Failed to clear dots-and-boxes auto-save:', error);
      return false;
    }
  }

  hasAutoSave(): boolean {
    return this.loadAutoSave() !== null;
  }

  saveGame(
    gameId: string,
    mode: DotsBoxesGameSettings['mode'],
    gameState: DotsBoxesGameState,
    metadata: SavedDotsBoxesGame['metadata']
  ): boolean {
    try {
      const savedGame: SavedDotsBoxesGame = {
        gameId,
        mode,
        gameState,
        metadata: { ...metadata, lastSavedAt: Date.now() },
      };

      const games = this.loadAllGames();
      const existingIndex = games.findIndex((g) => g.gameId === gameId);

      if (existingIndex >= 0) {
        games[existingIndex] = savedGame;
      } else {
        games.push(savedGame);
      }

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(games));
      return true;
    } catch (error) {
      console.error('Failed to save dots-and-boxes game:', error);
      return false;
    }
  }

  loadGame(gameId: string): SavedDotsBoxesGame | null {
    try {
      const games = this.loadAllGames();
      return games.find((g) => g.gameId === gameId) || null;
    } catch (error) {
      console.error('Failed to load dots-and-boxes game:', error);
      return null;
    }
  }

  loadAllGames(): SavedDotsBoxesGame[] {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Failed to load dots-and-boxes games:', error);
      return [];
    }
  }

  deleteGame(gameId: string): boolean {
    try {
      const games = this.loadAllGames();
      const filtered = games.filter((g) => g.gameId !== gameId);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filtered));
      return true;
    } catch (error) {
      console.error('Failed to delete dots-and-boxes game:', error);
      return false;
    }
  }

  clearAllGames(): boolean {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      localStorage.removeItem(this.AUTO_SAVE_KEY);
      return true;
    } catch (error) {
      console.error('Failed to clear all dots-and-boxes games:', error);
      return false;
    }
  }

  async syncFromCloud(): Promise<boolean> {
    try {
      if (!this.syncService) return false;
      console.warn('Cloud sync not yet implemented for dots-and-boxes games');
      return false;
    } catch (error) {
      console.error('Failed to sync dots-and-boxes games from cloud:', error);
      return false;
    }
  }

  async syncToCloud(): Promise<boolean> {
    try {
      if (!this.syncService) return false;
      console.warn('Cloud sync not yet implemented for dots-and-boxes games');
      return true;
    } catch (error) {
      console.error('Failed to sync dots-and-boxes games to cloud:', error);
      return false;
    }
  }
}
